import React, { useState, useRef, useEffect } from 'react';
import { Typography } from '../interfaces/typography';
import { HelpCircle, Search } from 'lucide-react';
import { createPortal } from 'react-dom';

// Standard border radius constants
export const STANDARD_BORDER_RADIUS = '8px'; // equivalent to Tailwind's rounded-lg
export const STANDARD_BORDER_RADIUS_CLASS = 'rounded-lg';

// Standard container styling constants
export const STANDARD_CONTAINER_CLASSES = 'bg-white border border-gray-200';
export const STANDARD_CONTAINER_STYLE = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb', // gray-200
  borderRadius: STANDARD_BORDER_RADIUS,
};

// Tooltip Component
export function Tooltip({ 
  children, 
  content, 
  placement = 'top' 
}: { 
  children: React.ReactNode; 
  content: string;
  placement?: 'top' | 'bottom' | 'bottom-left';
}) {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<NodeJS.Timeout>()

  const showTooltip = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = undefined
    }
    setIsVisible(true)
  }

  const hideTooltip = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 100) // Small delay to prevent flickering
  }

  const updatePosition = () => {
    if (!triggerRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft
    const scrollY = window.pageYOffset || document.documentElement.scrollTop
    
    // Get tooltip dimensions if available
    const tooltipRect = tooltipRef.current?.getBoundingClientRect()
    const tooltipWidth = tooltipRect?.width || 256 // fallback to w-64
    const tooltipHeight = tooltipRect?.height || 60 // reasonable fallback
    
    let top = 0
    let left = 0
    const spacing = 12 // Increased spacing between tooltip and trigger

    switch (placement) {
      case 'bottom':
        top = triggerRect.bottom + scrollY + spacing
        left = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipWidth / 2
        break
      case 'bottom-left':
        top = triggerRect.bottom + scrollY + spacing
        left = triggerRect.left + scrollX
        break
      case 'top':
      default:
        top = triggerRect.top + scrollY - tooltipHeight - spacing
        left = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipWidth / 2
        break
    }

    // Ensure tooltip stays within viewport with padding
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const padding = 16
    
    // Horizontal bounds checking
    if (left < padding) {
      left = padding
    } else if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding
    }

    // Vertical bounds checking - if tooltip would go off screen, flip it
    if (placement === 'top' && top < padding) {
      // Flip to bottom
      top = triggerRect.bottom + scrollY + spacing
    } else if (placement === 'bottom' && top + tooltipHeight > viewportHeight - padding) {
      // Flip to top
      top = triggerRect.top + scrollY - tooltipHeight - spacing
    }

    setPosition({ top, left })
  }

  useEffect(() => {
    if (isVisible) {
      // Initial position update
      updatePosition()
      
      // Small delay to recalculate after tooltip renders with content
      const timeoutId = setTimeout(() => {
        updatePosition()
      }, 10)
      
      // Update position on scroll/resize
      const handleUpdate = () => updatePosition()
      window.addEventListener('scroll', handleUpdate, true)
      window.addEventListener('resize', handleUpdate)
      
      return () => {
        clearTimeout(timeoutId)
        window.removeEventListener('scroll', handleUpdate, true)
        window.removeEventListener('resize', handleUpdate)
      }
    }
  }, [isVisible, placement])

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [])

  const getArrowPosition = () => {
    if (!triggerRef.current) return ''
    
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft
    const triggerCenter = triggerRect.left + scrollX + triggerRect.width / 2
    const arrowLeft = triggerCenter - position.left - 4 // 4px is half of arrow width (8px)
    
    switch (placement) {
      case 'bottom':
      case 'bottom-left':
        return `top-0 -mt-1 w-2 h-2 bg-gray-900 rotate-45 transform`
      case 'top':
      default:
        return `bottom-0 -mb-1 w-2 h-2 bg-gray-900 rotate-45 transform`
    }
  }

  const getArrowStyle = () => {
    if (!triggerRef.current) return {}
    
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft
    const triggerCenter = triggerRect.left + scrollX + triggerRect.width / 2
    const arrowLeft = triggerCenter - position.left - 4 // 4px is half of arrow width (8px)
    
    // Get tooltip width from ref if available
    const tooltipWidth = tooltipRef.current?.getBoundingClientRect().width || 256
    
    return {
      left: Math.max(8, Math.min(arrowLeft, tooltipWidth - 16)) // Keep arrow within tooltip bounds
    }
  }

  const tooltipContent = isVisible ? (
    <div 
      ref={tooltipRef}
      className="fixed z-[9999] w-64 max-w-xs"
      style={{ 
        top: position.top,
        left: position.left 
      }}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg relative">
        <Typography variant="smallParagraph" color="white" fontSize="11px">
          {content}
        </Typography>
        <div 
          className={`absolute ${getArrowPosition()}`}
          style={getArrowStyle()}
        ></div>
      </div>
    </div>
  ) : null

  return (
    <>
      <div 
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {children}
      </div>
      {typeof document !== 'undefined' && tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  )
}

// Clean Select Component
export type SelectOption = {
  label: string;
  value: string;
}

export function Select({ 
  value, 
  onChange, 
  options, 
  placeholder = "Select...",
  className = "" 
}: { 
  value: string; 
  onChange: (value: string) => void; 
  options: SelectOption[];
  placeholder?: string;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`rounded-md border border-gray-300 bg-white px-3 py-1.5 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${className}`}
      style={{
        fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
        fontSize: '12px',
        fontWeight: 400,
        color: '#374151',
        lineHeight: '18px',
        minWidth: '120px'
      }}
    >
      {options.map((option) => (
        <option 
          key={option.value} 
          value={option.value}
          style={{
            fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
            fontSize: '12px',
            fontWeight: 400
          }}
        >
          {option.label}
        </option>
      ))}
    </select>
  )
}

// Clean Modern Select Component with better styling
export type CleanSelectOption = {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export function CleanSelect({ 
  value, 
  onChange, 
  options, 
  placeholder = "Select...",
  className = "",
  disabled = false
}: { 
  value: string | number; 
  onChange: (value: string | number) => void; 
  options: CleanSelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`
          w-full px-2 py-1 pr-7 
          border border-gray-300 rounded-md 
          bg-white text-gray-700
          shadow-sm hover:shadow-md
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          hover:border-gray-400
          transition-all duration-200
          appearance-none cursor-pointer
          disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50
        `}
        style={{
          fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
          fontSize: '12px',
          fontWeight: 500,
          color: '#374151',
          lineHeight: '18px',
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: 'right 6px center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '14px'
        }}
      >
        {options.map((option) => (
          <option 
            key={option.value} 
            value={option.value}
            disabled={option.disabled}
            style={{
              fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              color: option.disabled ? '#9ca3af' : '#374151'
            }}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

// Custom Dropdown Component with better styling
export function CustomDropdown({ 
  value, 
  onChange, 
  options, 
  placeholder = "Select...",
  className = "",
  disabled = false
}: { 
  value: string | number; 
  onChange: (value: string | number) => void; 
  options: CleanSelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Find selected option
  const selectedOption = options.find(opt => opt.value === value);

  const handleOptionClick = (optionValue: string | number) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-2 py-1 pr-7 text-left
          border border-gray-300 rounded-md 
          bg-white text-gray-700
          shadow-sm hover:shadow-md
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          hover:border-gray-400
          transition-all duration-200
          cursor-pointer
          disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50
        `}
        style={{
          minHeight: '30px'
        }}
      >
        <span className="block truncate">
          <Typography variant="smallParagraph" color="#374151" fontWeight="500">
            {selectedOption ? selectedOption.label : placeholder}
          </Typography>
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg 
            className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" 
            viewBox="0 0 20 20"
          >
            <path 
              stroke="currentColor" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="1.5" 
              d="m6 8 4 4 4-4"
            />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div 
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto"
          style={{
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => !option.disabled && handleOptionClick(option.value)}
              disabled={option.disabled}
              className={`
                w-full px-3 py-2 text-left
                hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
                ${value === option.value ? 'bg-blue-50' : ''}
              `}
            >
              <Typography 
                variant="smallParagraph" 
                color={value === option.value ? "#1d4ed8" : "#374151"} 
                fontWeight="500"
              >
                {option.label}
              </Typography>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Search Input Component
export function SearchInput({ 
  value, 
  onChange, 
  placeholder = "Search...",
  className = "" 
}: { 
  value: string; 
  onChange: (value: string) => void; 
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-8 pr-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        style={{
          fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
          fontSize: '12px',
          fontWeight: 400,
          color: '#374151',
          lineHeight: '18px'
        }}
      />
    </div>
  )
}

// Settings Section Container
export function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <Typography variant="h3" color="#1F2937" fontWeight={600} className="mb-7">
        {title}
      </Typography>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  )
}

// Setting Row Component
export type SettingRowProps = {
  label: string
  options?: { label: string; value: string }[]
  value: boolean | string | null
  setValue: (value: boolean | string) => void
  type?: 'select' | 'toggle'
  tooltip?: string
  disabled?: boolean
  loading?: boolean
}

export function SettingRow({ label, options, value, setValue, type = 'select', tooltip, disabled = false, loading = false }: SettingRowProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Typography variant="mediumParagraph" color="#374151" fontWeight={500}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip content={tooltip}>
            <HelpCircle size={14} className="text-gray-400 hover:text-gray-500 cursor-help" />
          </Tooltip>
        )}
        {loading && (
          <div className="flex items-center">
            <svg className="animate-spin h-3 w-3 text-blue-600 ml-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>
      
      {type === 'select' && options && (
        <select
          value={value as string}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled || loading}
          className={`min-w-[140px] rounded-md border border-gray-300 bg-white px-3 py-1.5 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
            (disabled || loading) ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          style={{
            fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            color: '#374151',
            lineHeight: '18px'
          }}
        >
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              style={{
                fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                fontSize: '12px',
                fontWeight: 400
              }}
            >
              {option.label}
            </option>
          ))}
        </select>
      )}
      
      {type === 'toggle' && (
        <button
          onClick={() => setValue(!value)}
          disabled={disabled || loading}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            value ? 'bg-blue-600 focus:ring-blue-500' : 'bg-gray-300 focus:ring-gray-300'
          } ${(disabled || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span
            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
              value ? 'translate-x-5' : 'translate-x-1'
            }`}
          />
        </button>
      )}
    </div>
  )
}

// Standard Container Layout (no styling, just spacing)
export function StandardContainer({ children, maxWidth = 'max-w-3xl' }: { 
  children: React.ReactNode; 
  maxWidth?: string;
}) {
  return (
    <div className="h-full w-full bg-gray-50 overflow-auto">
      <div className={`${maxWidth} mx-auto p-6 space-y-6`}>
        {children}
      </div>
    </div>
  )
}

// Container with white background and border
export function StandardContainerWithBorder({ children, maxWidth = 'max-w-3xl' }: { 
  children: React.ReactNode; 
  maxWidth?: string;
}) {
  return (
    <div className="h-full w-full bg-gray-50 overflow-auto">
      <div className={`${maxWidth} mx-auto p-6`}>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          {children}
        </div>
      </div>
    </div>
  )
}

// Full width container (no max-width constraint)
export function FullWidthContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full w-full bg-gray-50 overflow-auto">
      <div className="p-6 space-y-6">
        {children}
      </div>
    </div>
  )
}

// Full width container with border
export function FullWidthContainerWithBorder({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full w-full bg-gray-50 overflow-auto">
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          {children}
        </div>
      </div>
    </div>
  )
}

interface PrimaryActionButtonProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}

export const PrimaryActionButton: React.FC<PrimaryActionButtonProps> = ({
  children,
  icon,
  onClick,
  disabled = false,
  className = ''
}) => {
  return (
    <button 
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-2 py-1 bg-black/80 hover:bg-black/90 disabled:bg-black/40 text-white rounded-lg font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-black/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {icon}
      <Typography variant="mediumParagraph" color="inherit" fontWeight={500}>
        {children}
      </Typography>
    </button>
  );
};

// Re-export access control components for convenience
export { RequiresAdminAccess, RequiresPaidAccess, RequiresParallelAccess } from './AccessControl'; 